/**
 * DetailDrawer (revised for Figma parity).
 *
 * Render types:
 *   'briefing'            — factual key-metrics view
 *   'spending-breakdown'  — NEW: FINANCIAL BRIEFING DETAIL / Spending breakdown
 *   'committed'           — NEW: Committed money review
 *   'attention-payment'   — upcoming payment (navigate-only CTA)
 *   'attention-recurring' — recurring change
 *   'goal'                — goal with % + on-track pill + monthly contributions
 *   'reflection'          — reflection prompt
 */
import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { formatMoney } from '@/lib/format';
import { ArrowDown, ArrowUp } from 'lucide-react';
import SpendingExplorer from '@/components/spending/SpendingExplorer';

const META = {
  'briefing':            { label: 'FINANCIAL BRIEFING',        title: 'Monthly briefing' },
  'spending-breakdown':  { label: 'FINANCIAL BRIEFING DETAIL', title: 'Spending breakdown' },
  'committed':           { label: 'COMMITTED MONEY',           title: 'Review committed money' },
  'attention-payment':   { label: 'UPCOMING PAYMENT',          title: 'Upcoming payment' },
  'attention-recurring': { label: 'RECURRING CHANGE',          title: 'Recurring change' },
  'goal':                { label: 'GOAL PROGRESS',             title: 'Goal details' },
  'reflection':          { label: 'REFLECTION',                title: 'Reflection' },
  // Phase 84.7 — Rich drawer for the Overview hero's Next Best Action.
  // Restores the drawer the user asked for (they wanted a detail sheet,
  // then a navigation button — not a raw route jump). Body renders the
  // NBA headline, "Why this action" narrative, expected benefit, and
  // consider-before-acting caveat pulled from the backend NBA payload.
  'next-best-action':    { label: 'NEXT BEST ACTION',          title: 'Next best action' },
};

/**
 * Phase 86.C — the hand-rolled spending list that used to live here is gone.
 * It read pre-computed `categories[]` off /api/dashboard/breakdown and built its
 * own "what changed" sentence, which meant the dashboard and an account's
 * Analysis tab answered the same question with different code. Both now render
 * the shared <SpendingExplorer>; the dashboard simply passes no account scope,
 * so it covers every eligible spending account.
 */
function SpendingBreakdownBody({ baseCurrency }) {
  return (
    <SpendingExplorer
      baseCurrency={baseCurrency}
      scopeLabel="All spending accounts"
      compact
      defaultPeriod="this_month"
      testidPrefix="dashboard-spending-explorer"
    />
  );
}

function CommittedBody({ data, baseCurrency, onNavigate }) {
  const d = data || {};
  const total = d.total || 0;
  const categories = d.categories || [];
  
  // Generate colors for categories (simple palette)
  const colors = ['#8B7355', '#A0826D', '#B8956A', '#C9A86A', '#D4B896'];
  
  return (
    <div className="space-y-5">
      <div>
        <div className="num text-3xl font-display font-semibold text-foreground">{formatMoney(total, baseCurrency)}<span className="text-base text-muted-foreground font-normal">/month</span></div>
        {d.pctOfIncome != null && <p className="text-sm text-muted-foreground mt-1">{Math.round(d.pctOfIncome)}% of monthly income</p>}
      </div>
      {categories.length > 0 && (
        <>
          <div className="h-2.5 rounded-full overflow-hidden flex bg-muted">
            {categories.map((c, idx) => {
              const pct = total > 0 ? ((c.amount || 0) / total) * 100 : 0;
              return <div key={c.name || idx} style={{ width: `${pct}%`, backgroundColor: colors[idx % colors.length] }} />;
            })}
          </div>
          <ul className="space-y-2.5" data-testid="drawer-committed-categories">
            {categories.map((c, idx) => (
              <li key={c.name || idx} className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2.5 text-sm text-foreground">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: colors[idx % colors.length] }} />
                  {c.name}
                </span>
                <span className="num text-sm text-foreground">{formatMoney(c.amount || 0, baseCurrency)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {d.note && (
        <div className="flex gap-2 pt-3 border-t border-border/60">
          <span className="h-1.5 w-1.5 rounded-full bg-gold mt-2 shrink-0" />
          <p className="text-xs text-muted-foreground">{d.note}</p>
        </div>
      )}
      <Button className="w-full" onClick={() => onNavigate('/recurring')} data-testid="drawer-committed-open-recurring">Open recurring</Button>
    </div>
  );
}

function PaymentBody({ data, baseCurrency, onNavigate }) {
  const c = data || {};
  // Phase 84.22.7 — Show the commitment in its NATIVE currency (INR for
  // an Indian home-loan EMI, INR for a Groww SIP, AED for a Dubai rent
  // charge). Previously we rendered `monthly_equivalent_base` labelled
  // as `baseCurrency`, which produced factually wrong strings like
  // "AED 32,000" for a ₹32,000 EMI. Native amounts match every other
  // surface the user sees (Recurring page, Transactions row, imported
  // statement). Fallback to base only if we don't have a native amount.
  const nativeAmt = Number(c.amount);
  const nativeCur = (c.currency || '').toUpperCase();
  const showNative = Number.isFinite(nativeAmt) && nativeAmt > 0 && nativeCur;
  const amountText = showNative
    ? formatMoney(nativeAmt, nativeCur)
    : formatMoney(c.monthly_equivalent_base || c.amount_base || 0, baseCurrency);
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium">Commitment</div>
        <div className="text-lg font-semibold text-foreground">{c.name || 'Payment'}</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><div className="text-[10px] uppercase text-muted-foreground">Amount</div><div className="num text-base font-semibold text-foreground">{amountText}</div></div>
        <div><div className="text-[10px] uppercase text-muted-foreground">Next due</div><div className="text-base text-foreground">{c.next_due ? new Date(c.next_due).toLocaleDateString() : '—'}</div></div>
      </div>
      <Button className="w-full" onClick={() => onNavigate('/recurring')} data-testid="drawer-payment-open-recurring">Open in Recurring</Button>
    </div>
  );
}

function RecurringBody({ data, onNavigate }) {
  const s = data || {};
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[10px] uppercase text-muted-foreground">Detected stream</div>
        <div className="text-lg font-semibold text-foreground">{s.merchant || s.description || 'Recurring pattern'}</div>
      </div>
      {s.cadence && <div className="text-sm text-muted-foreground">Detected cadence: <span className="text-foreground">{s.cadence}</span></div>}
      <Button className="w-full" onClick={() => onNavigate('/recurring')} data-testid="drawer-recurring-review">Review recurring expenses</Button>
    </div>
  );
}

function GoalBody({ data, baseCurrency, monthlyContributions = [], onNavigate }) {
  const g = data || {};
  const pct = Math.round(g.progress_pct || 0);
  const onTrackPill = g.on_track === true
    ? { label: 'On track', className: 'bg-sage-light text-emerald' }
    : g.on_track === false
      ? { label: 'Off track', className: 'bg-amber/20 text-amber' }
      : null;
  const contribMax = monthlyContributions.reduce((m, c) => Math.max(m, c.amount_base || 0), 0) || 1;
  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-muted/50 p-5">
        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium">{(g.title || 'Goal').toUpperCase()}</div>
        <div className="mt-2 flex items-end justify-between gap-3">
          <div>
            <div className="font-display text-4xl text-foreground" data-testid="drawer-goal-pct">{pct}%</div>
            <div className="num text-xs text-muted-foreground mt-1">{formatMoney(g.current_amount_base || 0, baseCurrency)} of {formatMoney(g.target_amount_base || 0, baseCurrency)}</div>
          </div>
          {onTrackPill && <span className={`text-[11px] font-medium px-2.5 py-1 rounded-full ${onTrackPill.className}`}>{onTrackPill.label}</span>}
        </div>
        <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
          <div className="h-full bg-gold" style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
        </div>
      </div>

      {monthlyContributions.length > 0 && (
        <section>
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-3">Monthly Contributions</div>
          <ul className="space-y-3" data-testid="drawer-goal-monthly-contribs">
            {monthlyContributions.map((c) => (
              <li key={c.label} className="flex items-center gap-3 text-sm">
                <span className="w-9 text-muted-foreground">{c.label}</span>
                <span className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                  <span className="block h-full bg-gold" style={{ width: `${((c.amount_base || 0) / contribMax) * 100}%` }} />
                </span>
                <span className="num text-foreground">{formatMoney(c.amount_base || 0, baseCurrency)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Button className="w-full" onClick={() => onNavigate('/goals')} data-testid="drawer-goal-add-money">Add money to goal</Button>
    </div>
  );
}

function BriefingBody({ data, baseCurrency }) {
  const b = data || {};
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">A factual view of the metrics behind your month.</p>
      <div className="space-y-2">
        {(b.key_metrics || []).map((km) => (
          <div key={km.key} className="flex items-center justify-between border-b border-border/40 pb-2">
            <span className="text-sm text-foreground">{km.label}</span>
            <span className="num text-foreground text-sm">
              {km.value_base == null ? '—'
                : km.unit === 'currency' ? formatMoney(km.value_base, baseCurrency)
                : km.unit === 'percent' ? `${Math.round(km.value_base * 10) / 10}%`
                : km.unit === 'months' ? `${Math.round(km.value_base * 10) / 10} mo`
                : String(km.value_base)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReflectionBody({ data, onNavigate }) {
  const q = data || {};
  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium">Prompt</div>
      <p className="font-fraunces text-base text-foreground leading-snug">{q.question_text || q.text || 'Take a moment to reflect on your week.'}</p>
      <Button className="w-full" onClick={() => onNavigate('/reflections')} data-testid="drawer-reflection-open">Answer in Reflections</Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 84.7 — Next Best Action detail body.
//
// Renders a rich, structured view of the hero's NBA, matching the Figma
// design the user shared: headline, "why this action" narrative, expected
// benefit (from `cost_note`), a "consider before acting" caveat, and TWO
// primary actions:
//   • "Mark as done"       (secondary, closes the drawer for now — actual
//                           persistence is deferred to a follow-up phase
//                           once we have a dashboard.dismissed_nbas table)
//   • "Open <target page>" (primary, navigates to nba.cta.target)
//
// Falls closed if `data` is missing any field. The section is only
// rendered when we have signal for it.
// ---------------------------------------------------------------------------
// Phase 84.7.2 — When a briefing NBA arrives without an explicit
// `cta.target`, derive a sensible fallback route from the NBA's textual
// signal (kind / headline / context) so the drawer's primary CTA always
// creates an actionable loop instead of dead-ending on "Not now / Close".
function fallbackRouteFromNba(nba) {
  const blob = [
    nba && nba.kind,
    nba && nba.headline,
    nba && nba.context,
    nba && nba.cta && nba.cta.label,
  ].filter(Boolean).join(' ').toLowerCase();
  if (!blob) return '/dashboard';
  if (/recurring|subscription|emi|rent|bill/.test(blob)) return '/recurring';
  if (/goal/.test(blob))                                 return '/plan-ahead?tab=goals';
  if (/budget|spend|category|categor/.test(blob))        return '/insights?tab=budget';
  if (/reflect/.test(blob))                              return '/reflections';
  if (/import|statement|upload/.test(blob))              return '/import';
  if (/transaction|merchant/.test(blob))                 return '/transactions';
  if (/net.?worth|balance|account|invest/.test(blob))    return '/accounts';
  return '/dashboard';
}

function NextBestActionBody({ data, onNavigate, onClose }) {
  const nba = (data && data.nba) || null;
  const updateHeadline = data && data.update_headline;
  if (!nba) {
    return (
      <div className="text-sm text-muted-foreground">
        No next best action available right now.
      </div>
    );
  }
  // Phase 84.7.2 — Always derive a routable target. Prefer the backend-
  // provided `cta.target`; otherwise fall back to a kind-based mapping so
  // the primary button always leads somewhere useful.
  const target = (nba.cta && nba.cta.target) || fallbackRouteFromNba(nba);
  const ctaLabel = (nba.cta && nba.cta.label) || 'Continue';
  const context = nba.context;
  const costNote = nba.cost_note;

  return (
    <div className="space-y-5" data-testid="drawer-next-best-action">
      {/* Headline (main directive) */}
      <div>
        <p className="font-fraunces text-lg text-foreground leading-snug" data-testid="drawer-nba-headline">
          {nba.headline || 'Take the next best action.'}
        </p>
        {updateHeadline && updateHeadline !== nba.headline && (
          <p className="text-xs text-muted-foreground mt-1.5">Based on: {updateHeadline}</p>
        )}
      </div>

      {/* Why this action */}
      {context && (
        <section>
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-2">Why this action</div>
          <p className="text-sm text-foreground leading-relaxed">{context}</p>
        </section>
      )}

      {/* Expected benefit */}
      {costNote && (
        <section className="rounded-xl bg-muted/40 border border-border/60 p-3">
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-1">Expected benefit</div>
          <p className="text-sm text-foreground">{costNote}</p>
        </section>
      )}

      {/* Consider before acting */}
      <section className="rounded-xl border border-border/60 p-3">
        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-1">Consider before acting</div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          2120 bases this suggestion on the financial information currently available in your account. Numbers refresh weekly — treat this as guidance, not gospel.
        </p>
      </section>

      {/* Buttons */}
      <div className="space-y-2 pt-1">
        <Button
          className="w-full"
          onClick={() => onNavigate(target)}
          data-testid="drawer-nba-navigate"
        >
          {ctaLabel}
        </Button>
        <Button
          variant="ghost"
          className="w-full text-muted-foreground hover:text-foreground"
          onClick={onClose}
          data-testid="drawer-nba-dismiss"
        >
          Not now
        </Button>
      </div>
    </div>
  );
}

export default function DetailDrawer({ open, type, data, baseCurrency = 'INR', onOpenChange, monthlyContributions = [] }) {
  const navigate = useNavigate();
  const go = (path) => { onOpenChange?.(false); navigate(path); };

  const meta = META[type] || { label: '', title: 'Details' };

  let body = null;
  if (type === 'briefing')            body = <BriefingBody         data={data} baseCurrency={baseCurrency} />;
  if (type === 'spending-breakdown')  body = <SpendingBreakdownBody baseCurrency={baseCurrency} />;
  if (type === 'committed')           body = <CommittedBody        data={data} baseCurrency={baseCurrency} onNavigate={go} />;
  if (type === 'attention-payment')   body = <PaymentBody          data={data} baseCurrency={baseCurrency} onNavigate={go} />;
  if (type === 'attention-recurring') body = <RecurringBody        data={data} onNavigate={go} />;
  if (type === 'goal')                body = <GoalBody             data={data} baseCurrency={baseCurrency} monthlyContributions={monthlyContributions} onNavigate={go} />;
  if (type === 'reflection')          body = <ReflectionBody       data={data} onNavigate={go} />;
  if (type === 'next-best-action')    body = <NextBestActionBody   data={data} onNavigate={go} onClose={() => onOpenChange?.(false)} />;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={`w-full overflow-y-auto p-0 ${type === 'spending-breakdown' ? 'sm:max-w-xl' : 'sm:max-w-md'}`}
        data-testid="dashboard-detail-drawer"
      >
        <div className="p-6 border-b border-border/60">
          <SheetHeader className="space-y-1">
            {meta.label && <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium">{meta.label}</div>}
            <SheetTitle className="font-display text-xl">{meta.title}</SheetTitle>
            <SheetDescription className="sr-only">Detail view for the selected dashboard item.</SheetDescription>
          </SheetHeader>
        </div>
        <div className="p-6">{body}</div>
        <div className="px-6 pb-6">
          <button type="button" onClick={() => onOpenChange?.(false)} className="w-full text-center text-sm text-muted-foreground hover:text-foreground py-2" data-testid="drawer-close">Close window</button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
